import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { REPORT_COMMENT_MAX_LENGTH } from '@/types/report-comments';

interface CommentComposerProps {
    placeholder: string;
    submitLabel: string;
    isSubmitting?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    onSubmit: (content: string) => Promise<void> | void;
    onCancel?: () => void;
}

export function CommentComposer({
    placeholder,
    submitLabel,
    isSubmitting = false,
    autoFocus = false,
    disabled = false,
    onSubmit,
    onCancel,
}: CommentComposerProps) {
    const [content, setContent] = useState('');

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const trimmedContent = content.trim();
        if (!trimmedContent || disabled) return;
        if (trimmedContent.length > REPORT_COMMENT_MAX_LENGTH) return;

        try {
            await onSubmit(trimmedContent);
            setContent('');
        } catch {
            return;
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            <Textarea
                value={content}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                    setContent(event.target.value)
                }
                placeholder={placeholder}
                autoFocus={autoFocus}
                maxLength={REPORT_COMMENT_MAX_LENGTH}
                disabled={disabled || isSubmitting}
                className="min-h-[92px] resize-none bg-background"
            />

            <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>Max. {REPORT_COMMENT_MAX_LENGTH} caracteres</span>
                <span>
                    {content.length}/{REPORT_COMMENT_MAX_LENGTH}
                </span>
            </div>

            <div className="flex items-center justify-end gap-2">
                {onCancel && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onCancel}
                        disabled={disabled || isSubmitting}
                    >
                        Cancelar
                    </Button>
                )}
                <Button type="submit" disabled={disabled || isSubmitting || !content.trim()}>
                    {submitLabel}
                </Button>
            </div>
        </form>
    );
}
